<template>
    <section class="product-hero">
        <p
            class="h1 name d-none d-lg-block"
            v-test:shortTitle
        >
            {{ name }}
        </p>

        <div class="media">
            <div class="media-header">
                <dietary-labels
                    v-if="dietaryLabels.length"
                    :items="dietaryLabels"
                    large
                />
            </div>

            <div
                class="image-wrapper"
                :class="{
                    'grocery-image-wrapper': productType === 'grocery',
                }"
            >
                <lazy-image
                    class="main-image"
                    :src="image.src"
                    :alt="image.alt"
                    :srcset="imageSm.src ? `${ imageSm.src } 575w, ${ image.src }` : ''"
                    :width="image.width"
                    :height="image.height"
                    v-test:mainImg
                />

                <story-collapse-button
                    v-if="hasStory"
                    :product-type="productType"
                    :class="{
                        'grocery-collapse-button': productType === 'grocery',
                    }"
                />
            </div>
        </div>

        <div class="info">
            <div class="header">
                <div class="h2 info-header">
                    <h1
                        class="h2 secondary-name"
                        v-test:name
                    >
                        {{ name }}
                    </h1>

                    <p
                        v-if="weight"
                        class="weight"
                        v-test:weight
                    >
                        {{ weight }}
                    </p>
                </div>
            </div>

            <p
                class="description"
                v-test:description
            >
                {{ description }}
            </p>

            <p class="calories">
                <span v-test:calories>{{ calories }}</span>
            </p>
        </div>
    </section>
</template>

<script>
    import DietaryLabels from '../../atoms/DietaryLabels/DietaryLabels';
    import StoryCollapseButton from '../StoryCollapseButton/StoryCollapseButton';

    export default {
        name: 'product-hero',

        components: { StoryCollapseButton, DietaryLabels },

        props: {
            name: {
                type: String,
                required: true,
            },

            description: {
                type: String,
                required: true,
            },

            calories: {
                type: [Number, String],
                required: true,
            },

            weight: {
                type: String,
                default: '',
            },

            image: {
                type: Object,
                required: true,
            },

            imageSm: {
                type: Object,
                default: () => ({}),
            },

            dietaryLabels: {
                type: Array,
                default: () => ([]),
            },

            productType: {
                type: String,
                default: 'menu',
            },

            buttonLink: {
                type: String,
                required: true,
            },

            buttonText: {
                type: String,
                required: true,
            },

            hasStory: {
                type: Boolean,
                default: false,
            },
        },
    };
</script>

<style lang="scss" scoped>
    ::v-deep {
        @import "~bootstrap/scss/utilities/display";
    }

    .product-hero {
        display: flex;
        flex-direction: column;
        background-color: inherit;
    }

    .name {
        font-size: rem(32px);
        line-height: rem(36px);
        margin-bottom: 23px;
        order: 2;

        @include media-breakpoint-up(lg) {
            order: 1;
            padding-bottom: 10px;
            border-bottom: 2px solid $black;
            margin-bottom: 30px;
            font-size: rem(48px);
            line-height: rem(56px);
        }
    }

    .media {
        margin-bottom: 20px;
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-direction: column;
        order: 1;
        background-color: inherit;

        @include media-breakpoint-up(lg) {
            margin-bottom: 35px;
            order: 2;
        }
    }

    .media-header {
        justify-content: center;
        margin-bottom: 5px;
        min-height: 24px;

        @include media-breakpoint-up(md) {
            min-height: 44px;
        }
    }

    .image-wrapper {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        flex-grow: 1;
    }

    .grocery-image-wrapper {
        position: static;
    }

    .main-image {
        max-width: max-content;
        object-fit: contain;
        max-height: 200px;

        @include media-breakpoint-up(md) {
            max-height: 350px;
        }

        @include media-breakpoint-up(lg) {
            max-height: 470px;
        }
    }

    .story-collapse-button:not(.grocery-collapse-button) {
        position: absolute;
        left: 100%;
        width: 100px;
        transform: translateX(-50%);
        margin-left: 10px;
        bottom: 10px;

        @include media-breakpoint-up(md) {
            width: 200px;
            margin-left: 20px;
            bottom: 0;
        }

        @include media-breakpoint-up(lg) {
            width: 250px;
        }

        @include media-breakpoint-up(xl) {
            width: 300px;
            bottom: -30px;
        }
    }

    .grocery-collapse-button {
        position: absolute;
        right: 0;
        bottom: 0;
        max-width: 75px;

        @include media-breakpoint-up(md) {
            max-width: 150px;
        }

        @include media-breakpoint-up(lg) {
            max-width: 175px;
        }
    }

    .info {
        order: 2;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;

        @include media-breakpoint-up(md) {
            margin-bottom: 6px;
        }
    }

    .secondary-name {
        font-size: rem(32px);
        line-height: rem(34px);
        font-weight: 700;
    }

    .description {
        font-size: rem(20px);
        line-height: rem(24px);
        margin-bottom: 8px;
        max-width: 500px;
    }

    .weight {
        font-size: 1rem;
        font-weight: normal;
        margin: 0;

        @include media-breakpoint-up(md) {
            white-space: nowrap;
        }
    }

    .info-header {
        line-height: inherit;
        margin-bottom: 0;

        @include media-breakpoint-up(md) {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }

        .h2 {
            @include media-breakpoint-up(md) {
                margin-bottom: 0;
                margin-right: 30px;

                &:last-child {
                    margin-right: 0;
                }
            }
        }
    }
</style>
